<script setup>

import Button from "@/components/Button.vue";
import Input from "@/components/Input.vue";
import ButtonDropdownFilter from "@/components/ButtonDropdownFilter.vue";
import {mailActions} from "@/Modules/Mail/views/Mail/composables/mailActions";
import {computed} from "vue";
import {useI18n} from "vue-i18n";

const { t } = useI18n({ useScope: 'global' })

const emits = defineEmits(['handleAction'])

const filter = defineModel('filter', {default: ''})
const search = defineModel('search', {default: ''})

const props = defineProps({
  allAction: { type: Boolean, default: true },
  showSchedule: { type: Boolean, default: false },
  isDraftFolder: { type: Boolean, default: false },
  selectedRowsLength: { type: Number, default: 1 },
  loadingReply: { type: Boolean, default: false },
  loadingShare: { type: Boolean, default: false },
  loadingEdit: { type: Boolean, default: false },
  loadingDestroy: { type: Boolean, default: false },
  folders: {type: Array, required: true},
  currentMail: Object,
  setHideTable: Function,
  loadUseSearch: Function,
})

const filterData = computed(() => [
  {label: t('mail.all'), value: ''},
  {label: t('mail.mail_unread'), value: "unread"},
  {label: t('mail.mail_flagged'), value: "FLAGGED"},
  {label: t('mail.mail_read'), value: 'read'},
  {label: t('mail.mail_unanswered'), value: 'UNANSWERED'}
])

function makeAction(action, params = null|{}) {
  emits('handleAction', {action, params})
}

const hasCurrent = computed(() => !!props.currentMail?.id)


</script>

<template>
  <div class="card-header py-4">
    <div class="d-flex justify-content-between align-items-center w-100">
      <div v-if="allAction" class="d-flex align-items-center">
        <div class="btn-group" role="group" aria-label="Basic example">
          <button type="button" class="btn btn-sm" @click.prevent="makeAction(mailActions.REPLAY)" :disabled="!(selectedRowsLength === 1 && (!showSchedule && !isDraftFolder)) || !hasCurrent" style="background-color: #eef3f7; border-top-left-radius: 5px; border-bottom-left-radius: 5px; cursor: pointer;">
            <i class="fa fa-reply" :title="$t('mail.ans')" :style="{ color: loadingReply ? '' : '#303D4E' }"></i>
          </button>
          <button type="button" class="btn btn-sm" style="background-color: #eef3f7;" :disabled="!((selectedRowsLength === 1 && !showSchedule) && !isDraftFolder) || !hasCurrent" @click.prevent="makeAction(mailActions.REPLAY_ALL)">
            <i class="fa fa-reply-all" :title="$t('mail.ans_all')"  :style="{ color: loadingReply ? '' : '#303D4E' }"></i>
          </button>
          <button type="button" class="btn btn-sm" style="background-color: #eef3f7;" :disabled="!((selectedRowsLength === 1 && !showSchedule) && !isDraftFolder) || !hasCurrent" @click.prevent="makeAction(mailActions.SHARE)">
            <i class="fas fa-arrow-right" :title="$t('mail.transfer')" :style="{ color: loadingShare ? '' : '#303D4E' }"></i>
          </button>
          <button type="button" class="btn btn-sm" style="background-color: #eef3f7;border-top-left-radius: 5px; border-bottom-left-radius: 5px" :disabled="!((selectedRowsLength === 1 && showSchedule) && !isDraftFolder) || !hasCurrent" @click.prevent="makeAction(mailActions.SHARE, null)">
            <i class="fas fa-edit" :title="$t('button.edit')" :style="{ color: loadingEdit ? '' : '#303D4E' }"></i>
          </button>
        </div>
        <button class="btn btn-sm mx-3" style="background-color: #eef3f7;" @click.prevent="makeAction(mailActions.REFRESH)">
          <i class="fas fa-sync" style="color: #303D4E;" :title="$t('mail.refresh')"></i>
        </button>
        <div class="dropdown" >
          <button :disabled="!((selectedRowsLength > 0 && !showSchedule) && !isDraftFolder) || !hasCurrent" class="btn btn-sm me-3 dropdown-toggle" type="button" id="dropdownMenuButton" :title="$t('mail.mark')" data-bs-toggle="dropdown" aria-expanded="false" style="background-color: #eef3f7;">
            <i class="fas fa-sliders-h" style="color: #303D4E;"></i>
          </button>
          <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
            <a class="dropdown-item" @click.prevent="makeAction(mailActions.MAKE_READ)" style="cursor: pointer;">{{$t('mail.read')}}</a>
            <a class="dropdown-item" @click.prevent="makeAction(mailActions.MAKE_UNREAD)" style="cursor: pointer;">{{$t('mail.unread')}}</a>
            <a class="dropdown-item" @click.prevent="makeAction(mailActions.MAKE_IMPORTANT)" style="cursor: pointer;">{{$t('mail.important')}}</a>
            <a class="dropdown-item" @click.prevent="makeAction(mailActions.MAKE_NOT_IMPORTANT)" style="cursor: pointer;">{{$t('mail.notImportant')}}</a>
          </div>
        </div>
        <div class="dropdown" >
          <button :disabled="!((selectedRowsLength > 0 && !showSchedule) && !isDraftFolder) || !hasCurrent" class="btn btn-sm me-3 dropdown-toggle" type="button" id="dropdownMenuChange" data-bs-toggle="dropdown" aria-expanded="false" style="background-color: #eef3f7;">
            {{$t('mail.change_folder')}}
          </button>
          <div class="dropdown-menu" aria-labelledby="dropdownMenuChange">
            <a class="dropdown-item" v-for="(folder, index) in folders" style="cursor: pointer;" :key="index" @click="makeAction(mailActions.CHANGE_FOLDER,{title: folder.title})">{{folder.title}}</a>
          </div>
        </div>
        <div class="dropdown">
          <button :disabled="!(selectedRowsLength === 1) || !hasCurrent" class="btn btn-sm me-3 dropdown-toggle" type="button" id="dropdownMenuButton" :title="$t('mail.more_actions')" data-bs-toggle="dropdown" aria-expanded="false" style="background-color: #eef3f7;">
            <i class="fas fa-cogs" style="color: #303D4E;"></i>
          </button>
          <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
            <a class="dropdown-item" @click.stop="makeAction(mailActions.PRINT)" style="cursor: pointer;">{{$t('mail.print')}}</a>
          </div>
        </div>
      </div>
      <div v-else class="d-flex align-items-center">
        <h3>{{$t('mail.subject')}} : {{currentMail?.subject}}</h3>
      </div>
      <div class="d-flex ms-auto">
        <ButtonDropdownFilter
            :options="filterData"
            value-field="value"
            name-field="label"
            v-model="filter"
            :border="true"
            v-if="!showSchedule && allAction"
            btnClass="btn-sm"
            :search="false"
        />
        <button class="btn btn-sm rounded-2" v-if="!allAction" :title="$t('button.back')" style="background-color: #eef3f7;" @click.prevent="setHideTable(false)">
          <i class="fas fa-arrow-left fs-5" style="color: #303D4E;"></i>
        </button>

        <Input
            container-class=""
            size="sm"
            inputClass="form-control form-control-md"
            name="input-search"
            :isInputGroup="true"
            :placeholder="$t('label.search')"
            :action="loadUseSearch"
            v-model="search"
            v-if="allAction && !showSchedule"
        >
          <template #rightAddon>
            <span class="position-absolute border-0 bg-transparent end-0 top-50 translate-middle" @click.prevent="loadUseSearch">
              <i class="fa fa-search"></i>
            </span>
          </template>
        </Input>
      </div>
    </div>
    <div class="d-flex justify-content-end w-100 mt-2">
      <Button btn-class="btn btn-danger btn-sm me-1" v-if="allAction && selectedRowsLength > 0" :is-loading="loadingDestroy" @click.prevent="makeAction(mailActions.DELETE)" btn-text="button.delete"></Button>
    </div>
  </div>
</template>

<style scoped>

</style>